import { CompoundBatch, CompoundBatchIngredients, CompoundBatchReviewFlag } from '@/models/Drug/CompoundDrug';
import { ASSAY_BASIS_LABELS, AssayBasis } from '@/models/Inventory';

/**
 * Display helpers for the compound batch record — the verification screen, the queue and the batch
 * history read the same ingredient snapshot and the same server-derived review flags, and describe
 * them in the same words.
 *
 * The flags themselves are never derived here: the server emits them from the batch record
 * (tfn_CompoundBatch_ReviewFlags) and refuses verification while any is unacknowledged. This module
 * only says what a flag means to the pharmacist reading it, and copes with a flag kind it has not
 * been taught (the gate holds either way).
 */

/** Batch fields the gate helpers read; a queue row and a full record both satisfy it. */
export type ReviewGateShape = Pick<CompoundBatch, 'reviewFlags' | 'reviewAcknowledgedBy'>;

/** The ingredient fields the record display reads. */
export type BatchIngredientRecordShape = Pick<CompoundBatchIngredients,
    'id' | 'drugName' | 'quantity' | 'unitOfMeasure' | 'lotNumber' | 'assayPercent' | 'assayBasis' | 'lodPercent'
    | 'fixedConversionFactor' | 'activityFactor' | 'scaledQuantity' | 'adjustedQuantity' | 'overrideQuantity'
    | 'overrideReason' | 'overrideBy' | 'overrideDate' | 'missingCoaFlag'>;

/** True while the batch carries review flags no pharmacist has acknowledged — the state in which the server refuses to verify it. */
export function requiresReviewAcknowledgement(batch: ReviewGateShape | null | undefined): boolean {
    if (!batch) return false;
    return (batch.reviewFlags?.length ?? 0) > 0 && !batch.reviewAcknowledgedBy;
}

/** Whether the ingredient row carries any activity-factor snapshot (rows saved with the feature off, and older batches, carry none). */
export function hasActivitySnapshot(ingredient: BatchIngredientRecordShape): boolean {
    return ingredient.scaledQuantity != null
        || ingredient.adjustedQuantity != null
        || ingredient.activityFactor != null
        || ingredient.overrideQuantity != null
        || ingredient.fixedConversionFactor != null
        || ingredient.missingCoaFlag === true;
}

/** True when the technician replaced the computed weigh-out by hand. */
export function isOverridden(ingredient: BatchIngredientRecordShape): boolean {
    return ingredient.overrideQuantity != null;
}

/** "99.7% Anhydrous · LOD 3.1%" — the COA recorded on the row, or blank when none was. */
export function describeIngredientCoa(ingredient: BatchIngredientRecordShape): string {
    const parts: string[] = [];
    if (ingredient.assayPercent != null) parts.push(`${ingredient.assayPercent}%`);
    if (ingredient.assayBasis && ingredient.assayBasis in ASSAY_BASIS_LABELS) {
        parts.push(ASSAY_BASIS_LABELS[ingredient.assayBasis as AssayBasis]);
    }
    let text = parts.join(' ');
    if (ingredient.lodPercent != null) text += `${text ? ' · ' : ''}LOD ${ingredient.lodPercent}%`;
    return text;
}

/** A recorded COA that did not drive the factor: the ingredient carried a fixed conversion factor, which replaces the COA outright. */
export function coaNotApplied(ingredient: BatchIngredientRecordShape): boolean {
    return ingredient.fixedConversionFactor != null && describeIngredientCoa(ingredient) !== '';
}

/** Formats a recorded quantity at the given precision; blank for null. */
export function formatRecordQuantity(value: number | null | undefined, decimalPlaces: number): string {
    if (value === null || value === undefined || !Number.isFinite(value)) return '';
    const places = Number.isInteger(decimalPlaces) && decimalPlaces >= 0 ? Math.min(decimalPlaces, 10) : 4;
    return value.toFixed(places);
}

/** How a review flag reads on the verification screen and in the batch history. */
export interface ReviewFlagDescription {
    /** The server's flag code, echoed so a consumer can style known kinds. */
    code: string;
    /** Short label — the badge text. */
    label: string;
    /** One sentence for the pharmacist: what happened and to which ingredient. */
    detail: string;
}

/** Badge text per flag kind; the code itself for a kind this build does not know. */
export function reviewFlagLabel(flagCode: string): string {
    switch (flagCode) {
        case 'MissingCoa': return 'No COA';
        case 'ManualOverride': return 'Overridden';
        default: return flagCode;
    }
}

/**
 * Describes one flag against the batch it belongs to. The ingredient row is looked up by the flag's
 * ingredient id so the sentence can quote the lot, the quantities and the override reason as saved.
 */
export function describeReviewFlag(flag: CompoundBatchReviewFlag,
                                   batch: Pick<CompoundBatch, 'ingredients'>,
                                   decimalPlaces: number = 4): ReviewFlagDescription {
    const ingredient = flag.compoundBatchIngredientID != null
        ? batch.ingredients?.find(row => row.id === flag.compoundBatchIngredientID) ?? null
        : null;
    const name = ingredient?.drugName || 'An ingredient';
    const unit = ingredient?.unitOfMeasure ?? '';
    const qty = (value: number | null | undefined) => `${formatRecordQuantity(value, decimalPlaces)} ${unit}`.trim();

    switch (flag.flagCode) {
        case 'MissingCoa': {
            const lot = ingredient?.lotNumber ? `lot ${ingredient.lotNumber}` : 'a lot';
            return {
                code: flag.flagCode,
                label: reviewFlagLabel(flag.flagCode),
                detail: `${name} is flagged for activity factor but was filled from ${lot} with no usable certificate of analysis — factor 1.0 applied, weigh-out ${qty(ingredient?.quantity)}.`,
            };
        }
        case 'ManualOverride': {
            const computed = ingredient?.adjustedQuantity ?? ingredient?.scaledQuantity ?? null;
            const who = ingredient?.overrideBy ? ` by ${ingredient.overrideBy}` : '';
            const when = ingredient?.overrideDate ? ` on ${new Date(ingredient.overrideDate).toLocaleString()}` : '';
            const reason = ingredient?.overrideReason ? ` Reason: ${ingredient.overrideReason}` : '';
            return {
                code: flag.flagCode,
                label: reviewFlagLabel(flag.flagCode),
                detail: `${name}: weigh-out overridden to ${qty(ingredient?.overrideQuantity)} in place of the computed ${qty(computed)}${who}${when}.${reason}`,
            };
        }
        default:
            return {
                code: flag.flagCode,
                label: reviewFlagLabel(flag.flagCode),
                detail: `${ingredient ? name : 'This batch'} carries a review flag (${flag.flagCode}) that must be acknowledged before verification.`,
            };
    }
}

/** Describes every flag on the batch, in the order the server returned them. */
export function describeReviewFlags(batch: Pick<CompoundBatch, 'ingredients' | 'reviewFlags'>,
                                    decimalPlaces: number = 4): ReviewFlagDescription[] {
    return (batch.reviewFlags ?? []).map(flag => describeReviewFlag(flag, batch, decimalPlaces));
}

/** One entry per flag kind with how many rows carry it — the queue's badge list. */
export interface ReviewFlagSummary {
    code: string;
    label: string;
    count: number;
}

export function summarizeReviewFlags(flags: CompoundBatchReviewFlag[] | null | undefined): ReviewFlagSummary[] {
    const summary: ReviewFlagSummary[] = [];
    for (const flag of flags ?? []) {
        const existing = summary.find(entry => entry.code === flag.flagCode);
        if (existing) existing.count += 1;
        else summary.push({ code: flag.flagCode, label: reviewFlagLabel(flag.flagCode), count: 1 });
    }
    return summary;
}
